// 🔧 event.js
// ✅ Purpose: Page for creating or editing events (by super admin or owner)
// ✅ Features:
// - Auto-generates slug from event name
// - Confirms Voting Type changes before applying them
// - Sets the logged-in creator as the original event owner
// - Creates new event if no editId route param is present
// - Fully validated and styled form with 20-second feedback banners

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  collection,
  query,
  where,
  limit,
} from "firebase/firestore";
import { useNavigate, useParams   } from "react-router-dom";
import { db, functions } from "../../firebase";
import {
  httpsCallable,
} from "firebase/functions";
import { useAuth } from "../../auth_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import EventVisibilityControl from "../../components/event_visibility_control";
import { isEventPublic, getEventVisibilityFields } from "../../utils/event_visibility";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import { logger } from '../../utils/logger';
import {
  VOTING_TYPE_OPTIONS,
  DEFAULT_VOTING_TYPE,
} from "../../utils/voting_types";
import { EVENT_TYPES } from "../../constants/event_types";
import {
  ATTENDANCE_ESTIMATES,
  HOW_HEARD_OPTIONS,
} from "../../constants/request_intake_options";
import {
  LGV_POWER_UPS,
  getDefaultPurchasedAddOns,
} from "../../constants/lgv_features";
import {
  getNewlyEnabledDemoPowerUps,
  seedExistingDemoPowerUps,
} from "../../demo_engine/services/seed_existing_demo_powerups";

const EVENT_EDITOR_POWER_UPS = LGV_POWER_UPS.filter(
  ({ key }) => !["sponsors", "vendors"].includes(key)
);

const AdminEventEdit = () => {

  const { user, authLoading } = useAuth();
  const { editId } = useParams();
  const navigate = useNavigate();

  if (editId) logger.log("✏️ Editing event:", {
    value: editId
  });

  const [form, setForm] = useState({
    name: "",
    slug: "",
    startDate: "",
    endDate: "",
    eventLocation: "",
    eventWebsite: "",
    eventType: "car_show",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    description: "",
    phone: "",
    votingType: DEFAULT_VOTING_TYPE,
    attendanceEstimate: "not_sure",
    howHeardAboutUs: "",
    // 🧩 Core Event Features
    // ✅ Purpose:
    // - Controls optional visibility of included Event Package features
    // - These are NOT paid add-ons anymore
    // - The standard LGV Event Package includes all core event operations tools
    requestedModules: [],

    // ⚡ Power Ups
    // ✅ Purpose:
    // - Tracks paid add-ons separately from included core features
    // - Admins can manually enable these for free/admin-created events
    // - Managers will eventually purchase these through Stripe Checkout
    purchasedAddOns: getDefaultPurchasedAddOns(),

    // 🔎 Allow Google/Bing indexing of public event pages
    publicSeo: true,

    // 💳 LGV Package / Payment Details
    // ✅ Purpose:
    // - Read-only package/payment data from automatic Stripe event activation
    // - Lets admins quickly see what package was purchased
    lgvPackage: null,
    paymentStatus: "",
    demo: false,
    activatedFromPurchase: false,
  });

  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notAuthorized, setNotAuthorized] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savedPurchasedAddOns, setSavedPurchasedAddOns] = useState(
    getDefaultPurchasedAddOns()
  );
  const [resolvedEventId, setResolvedEventId] = useState("");

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 20000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // 🔁 Load event data if editing
  useEffect(() => {
    if (!user) return;

    const savedStatus = sessionStorage.getItem("eventEditStatus");

    if (savedStatus) {
      setStatus(savedStatus);
      sessionStorage.removeItem("eventEditStatus");
    }

    const loadEvent = async () => {
      if (!editId) {
        setLoading(false);
        return;
      }
      let snap = await getDoc(doc(db, "events", editId));
      if (!snap.exists()) {
        const matchingSlug = await getDocs(
          query(collection(db, "events"), where("slug", "==", editId), limit(1))
        );
        snap = matchingSlug.docs[0] || null;
      }

      if (!snap?.exists()) {
        setNotAuthorized(true);
        setLoading(false);
        return;
      }

      const event = snap.data();
      setResolvedEventId(snap.id);
      if (event.slug && editId !== event.slug) {
        navigate(`/event/${encodeURIComponent(event.slug)}`, { replace: true });
      }
      let startDate = "";
      let endDate = "";

      try {
        const raw = event.startDate;
        const dateObj = raw?.toDate ? raw.toDate() : new Date(raw);
        startDate = isNaN(dateObj.getTime()) ? "" : dateObj.toISOString().split("T")[0];
      } catch {
        startDate = "";
      }

      try {
        const rawEnd = event.endDate;
        const endObj = rawEnd?.toDate ? rawEnd.toDate() : rawEnd ? new Date(rawEnd) : null;
        endDate = endObj && !isNaN(endObj.getTime()) ? endObj.toISOString().split("T")[0] : "";
      } catch {
        endDate = "";
      }
      
      const purchasedAddOns = {
        ...getDefaultPurchasedAddOns(),
        ...(event.purchasedAddOns || {}),
        social: hasPurchasedAddOn(event, "social"),
        promotion: hasPurchasedAddOn(event, "social"),
        auction:
          event.purchasedAddOns?.auction === true ||
          event.auctionEnabled === true,
      };

      setSavedPurchasedAddOns(purchasedAddOns);
      setForm({
        name: event.name || "",
        slug: event.slug || "",
        startDate,
        eventWebsite: event.eventWebsite || event.marketingUrl || "",
        eventType: event.eventType || "car_show",
        description: event.description || "",
        votingType: event.votingType || DEFAULT_VOTING_TYPE,
        purchasedAddOns,
        endDate,
        eventLocation: event.eventLocation || "",
        contactName: event.eventContact?.name || "",
        contactEmail: event.eventContact?.email || "",
        contactPhone: event.eventContact?.phone || "",
        attendanceEstimate: event.attendanceEstimate || "not_sure",
        howHeardAboutUs: event.howHeardAboutUs || "",
        requestedModules: [],
        publicSeo: isEventPublic(event),

        // 💳 LGV Package / Payment Details
        // ✅ Purpose:
        // - Keeps package purchase details visible when editing an event
        lgvPackage: event.lgvPackage || null,
        paymentStatus: event.demo === true ? "demo" : event.paymentStatus || "",
        demo: event.demo === true,
        activatedFromPurchase: !!event.lgvPackage?.lgvOrderId,
      });    

      setEditing(true);
      setLoading(false);
    };

    loadEvent();
  }, [user, editId, navigate]);

  const getPurchasedAddOnNames = () => {
    return LGV_POWER_UPS.filter(
      (addOn) => form.purchasedAddOns?.[addOn.key] === true
    );
  };

  // =====================================================
  // 🔍 Check whether slug is already in use
  // =====================================================
  const checkSlugAvailability = async (slugValue) => {
    if (!slugValue?.trim()) {
      setSlugAvailable(true);
      return;
    }

    try {
      setCheckingSlug(true);

      const slugQuery = query(
        collection(db, "events"),
        where("slug", "==", slugValue.trim().toLowerCase()),
        limit(1)
      );

      const snap = await getDocs(slugQuery);

      // Editing current event? Allow same slug
      if (!snap.empty) {
        const existingEvent = snap.docs[0];

        if (editing && existingEvent.id === (resolvedEventId || editId)) {
          setSlugAvailable(true);
        } else {
          setSlugAvailable(false);
        }
      } else {
        setSlugAvailable(true);
      }
    } catch (err) {
      logger.error("❌ Slug lookup failed:", {
        value: err,
      });

      setSlugAvailable(false);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handlePurchasedAddOnChange = (addOnKey) => {
    setForm((prev) => ({
      ...prev,
      purchasedAddOns: {
        ...getDefaultPurchasedAddOns(),
        ...(prev.purchasedAddOns || {}),
        [addOnKey]: !prev.purchasedAddOns?.[addOnKey],
        ...(addOnKey === "social" ? { promotion: !prev.purchasedAddOns?.social } : {}),
      },
    }));
  };

  const handleChange = (e) => {

    const { name, value, type, checked } = e.target;

    // =====================================================
    // ⚠️ Confirm Voting Type Changes
    // Purpose:
    // - Prevent accidental changes to the voting engine.
    // - Voting type affects ballots, audit reports,
    //   leaderboards and event behavior.
    // =====================================================
    if (
      name === "votingType" &&
      editing &&
      value !== form.votingType
    ) {
      const confirmed = window.confirm(
        "Changing the Voting Type after an event has been created may affect ballots, reports, and voting behavior.\n\nAre you sure you want to continue?"
      );

      if (!confirmed) {
        return;
      }
    }

    const updated = {
      ...form,
      [name]: type === "checkbox" ? checked : value,
    };

    if (name === "name" && !editing) {
      const slug = value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "");

      updated.slug = slug;

      checkSlugAvailability(slug);
    }
    
    if (name === "slug") {
      updated.slug = value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "");

      checkSlugAvailability(updated.slug);
    }

    setForm(updated);
  };

  const createEventCallable =
  httpsCallable(
    functions,
    "createEvent"
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setError(null);

    if (!slugAvailable) {
      setError(
        "Please choose a unique slug before saving."
      );

      return;
    }

    const {
      name,
      slug,
      startDate,
      endDate,
      eventLocation,
      eventWebsite,
      contactName,
      contactEmail,
      contactPhone,
      votingType,
      eventType,
    } = form;

    if (
      !name ||
      !slug ||
      !startDate ||
      !eventLocation ||
      !contactName ||
      !contactPhone ||
      !contactEmail
    ) {
        setError("Please complete all required fields.");
      return;
    }

    // =====================================================
    // 👤 Event Creator Becomes Original Owner
    // Purpose:
    // - The authenticated user creating the event automatically
    //   becomes the event owner.
    // - Additional managers are invited later from
    //   Event Settings → Managers.
    // =====================================================
    const normalizedManagerEmail = String(user?.email || "")
      .trim()
      .toLowerCase();

    const resolvedOwnerId = user.uid;

    const selectedPowerUps = {
      ...getDefaultPurchasedAddOns(),
      ...(form.purchasedAddOns || {}),
    };
    const newlyEnabledDemoPowerUps = getNewlyEnabledDemoPowerUps({
      previous: savedPurchasedAddOns,
      next: selectedPowerUps,
    });

    try {
      let eventRef = null;

      const payload = {
        name,
        slug,
        startDate: new Date(startDate),
        endDate: endDate ? new Date(endDate) : null,
        eventLocation,
        eventWebsite,
        ownerId: resolvedOwnerId,
        managerIds: [resolvedOwnerId],
        description: form.description || "",
        eventContact: {
          name: contactName || "",
          email: contactEmail || "",
          phone: contactPhone || "",
        },     
        votingType: votingType || DEFAULT_VOTING_TYPE,
        eventType,
        attendanceEstimate: form.attendanceEstimate || "not_sure",
        howHeardAboutUs: form.howHeardAboutUs || "",
        // 🧩 Core Event Features
        // ✅ Purpose:
        // - Included Event Package features enabled for this event
        // - These are not paid add-ons
        requestedModules: [
          "sponsors",
          "vendors",
          "volunteers",
          "auditing",
        ],

        // ⚡ Power Ups
        // ✅ Purpose:
        // - Paid/admin-enabled revenue tools
        // - These fields are what future app gating should use
        purchasedAddOns: {
          ...selectedPowerUps,
        },

        // ✅ Backward compatibility
        // Purpose:
        // - Keeps existing auction pages/nav working during migration
        sponsorEnabled: selectedPowerUps.sponsors === true,
        sponsorsEnabled: selectedPowerUps.sponsors === true,
        vendorEnabled: selectedPowerUps.vendors === true,
        vendorsEnabled: selectedPowerUps.vendors === true,
        auctionEnabled: selectedPowerUps.auction === true,
        storeEnabled: selectedPowerUps.store === true,
        vendorPaymentsEnabled: selectedPowerUps.vendorPayments === true,
        vendorsPaymentsEnabled: selectedPowerUps.vendorPayments === true,
        sponsorPaymentsEnabled: selectedPowerUps.sponsorPayments === true,
        ticketsEnabled: selectedPowerUps.tickets === true,
        passportEnabled: selectedPowerUps.passport === true,
        socialEnabled: selectedPowerUps.social === true,

        // 🔎 Controls whether public event pages may be indexed
        ...getEventVisibilityFields(form.publicSeo),

        // 💳 Preserve LGV package/payment data
        // ✅ Purpose:
        // - Prevents manual event edits from accidentally wiping payment/package metadata
        ...(form.lgvPackage
          ? {
              lgvPackage: form.lgvPackage,
              paymentStatus: form.demo ? "demo" : form.paymentStatus || "paid",
            }
          : {}),

        updatedAt: serverTimestamp(),
      };

      logger.log("🆕 Creating new event...", {});

      //------------------------------------------------------
      // NEW EVENT
      //------------------------------------------------------

      if (!editing) {

        const result =
          await createEventCallable({
            name,
            slug,
            startDate,
            endDate,
            eventLocation,
            eventWebsite,
            contactName,
            contactEmail,
            contactPhone,
            votingType,
            eventType,
            description: form.description || "",
            attendanceEstimate: form.attendanceEstimate || "not_sure",
            howHeardAboutUs: form.howHeardAboutUs || "",
            ...getEventVisibilityFields(form.publicSeo),
            purchasedAddOns: {
              ...selectedPowerUps,
            },
          });

        const eventId =
          result.data.eventId;

        sessionStorage.setItem(
          "eventEditStatus",
          "✅ Event created successfully."
        );

        navigate(`/event/${encodeURIComponent(result.data.slug || slug)}`);

        return;

      }

      //------------------------------------------------------
      // EXISTING EVENT
      //------------------------------------------------------

      eventRef =
        doc(
          db,
          "events",
          resolvedEventId || editId
        );

      await setDoc(eventRef, payload, { merge: true });

      // Activate the Power Up before querying or writing its collection so
      // production Firestore rules authorize the Experience Engine work.
      // Only newly enabled Power Ups run, and the service detects older demo
      // records so a disable/re-enable cycle cannot create duplicates.
      if (form.demo === true && newlyEnabledDemoPowerUps.length > 0) {
        const powerUpNames = LGV_POWER_UPS.filter(({ key }) =>
          newlyEnabledDemoPowerUps.includes(key)
        ).map(({ name }) => name);

        setStatus(
          `Building demo content for ${powerUpNames.join(", ")}...`
        );

        const seedResult = await seedExistingDemoPowerUps({
          eventId: resolvedEventId || editId,
          powerUpKeys: newlyEnabledDemoPowerUps,
          user,
        });

        if (seedResult.processed.length > 0) {
          const demoSeededPowerUps = seedResult.processed.reduce(
            (markers, key) => ({
              ...markers,
              [key]: {
                seededAt: serverTimestamp(),
                source: "event_editor",
              },
            }),
            {}
          );

          await setDoc(
            eventRef,
            { demoSeededPowerUps, updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
      }

      setSavedPurchasedAddOns(selectedPowerUps);
      logger.log("✅ Event saved", {});

      // ✅ Assign manager role to this user in event user list
      try {
        await setDoc(doc(db, "events", eventRef.id, "users", resolvedOwnerId), {
          role: "manager",
          pendingManager: false,
          pendingUser: false,
          email: normalizedManagerEmail,
          updatedAt: serverTimestamp(),
        });
        logger.log("✅ Manager assigned", {});
      } catch (err) {
        logger.error("❌ Assigning manager failed:", {
          value: err
        });
      }

      logger.log("📩 Manager role assigned:", {
        value: resolvedOwnerId
      });

      const successMessage = editing
        ? "✅ Event updated successfully."
        : "✅ Event created successfully.";

      sessionStorage.setItem(
        "eventEditStatus",
        successMessage
      );

      if (editing) {
        window.location.reload();
      } else {
        navigate(`/event/${encodeURIComponent(form.slug || eventRef.id)}`);
      }

    } catch (err) {
      logger.error("❌ Failed to save event:", {
        value: err
      });
      setError("Something went wrong while saving.");
    }
  };
  
  if (authLoading || loading) return <FullPageSpinner message="Loading Event..." />;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">{editing ? "✏️ Edit Event" : "➕ Add New Event"}</h1>
          <p className="lead text-muted">{editing ? "Update existing event details" : "Set up a new event for voting and participation"}</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {/* =====================================================
            💳 LGV Package / Payment Summary
            Purpose:
            - Shows read-only payment/package information
            - Shows purchased/admin-enabled Power Ups
            - Helps admins quickly understand what this event owns
        ===================================================== */}
        {editing && form.lgvPackage && (
          <div className="alert alert-success text-start mb-4">
            <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-4">
              <div className="flex-shrink-0">
                <div className="small text-muted">Package</div>
                <div className="fw-bold">
                  {form.lgvPackage?.packageName || "LGV Event Package"}
                </div>
              </div>

              <div className="flex-shrink-0">
                <div className="small text-muted">Package Status</div>
                <div className="fw-bold text-success">
                  {form.lgvPackage?.status || "active"}
                </div>
              </div>

              <div className="flex-shrink-0">
                <div className="small text-muted">Payment Status</div>
                <div className="fw-bold text-success">
                  {form.demo ? "demo" : form.paymentStatus || "paid"}
                </div>
              </div>

              <div className="flex-grow-1">
                <div className="small text-muted">Power Ups</div>

                {getPurchasedAddOnNames().length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {getPurchasedAddOnNames().map((addOn) => (
                      <span key={addOn.key} className="badge bg-success">
                        {addOn.icon} {addOn.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">
                    No Power Ups enabled.
                  </div>
                )}
              </div>

              <div className="flex-shrink-0">
                <div className="small text-muted">LGV Order ID</div>
                <code>{form.lgvPackage?.lgvOrderId || "Not available"}</code>
              </div>

              <div className="flex-shrink-0">
                <a
                  href={`/${form.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  🌐 Open Event Home
                </a>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mb-5">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Event Name*</label>
              <input name="name" value={form.name} onChange={handleChange} className="form-control" />
            </div>

            <div className="mb-3">
              <label className="form-label">Event Type *</label>

              <select
                className="form-select"
                name="eventType"
                value={form.eventType}
                onChange={handleChange}
                required
              >
                {EVENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <div className="form-text">
                This helps us understand the best voting setup for your event.
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Estimated Attendance</label>

              <select
                name="attendanceEstimate"
                className="form-select"
                value={form.attendanceEstimate || "not_sure"}
                onChange={handleChange}
              >
                {ATTENDANCE_ESTIMATES.map((estimate) => (
                  <option key={estimate.value} value={estimate.value}>
                    {estimate.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">How did they hear about us?</label>

              <select
                name="howHeardAboutUs"
                className="form-select"
                value={form.howHeardAboutUs || ""}
                onChange={handleChange}
              >
                <option value="">Select one</option>

                {HOW_HEARD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Slug</label>

              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                className={`form-control ${
                  form.slug
                    ? slugAvailable
                      ? "is-valid"
                      : "is-invalid"
                    : ""
                }`}
                placeholder="event-url-slug"
              />

              {checkingSlug && (
                <div className="form-text">
                  Checking availability...
                </div>
              )}

              {!checkingSlug && form.slug && slugAvailable && (
                <div className="valid-feedback">
                  Slug is available.
                </div>
              )}

              {!checkingSlug && form.slug && !slugAvailable && (
                <div className="invalid-feedback">
                  This slug is already being used by another event.
                </div>
              )}

              <div className="form-text">
                Public URL:
                <br />
                https://letsgovote.app/{form.slug}
              </div>
            </div>
            <div className="col-md-12">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                className="form-control"
                rows={3}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Start Date*</label>
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">End Date</label>
              <input
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Event Location*</label>
              <input
                name="eventLocation"
                value={form.eventLocation}
                onChange={handleChange}
                className="form-control"
                placeholder="Venue, city, or city/state"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Voting Type*</label>
              <select
                name="votingType"
                value={form.votingType || DEFAULT_VOTING_TYPE}
                onChange={handleChange}
                className="form-select"
                //disabled={editing}
              >
                {VOTING_TYPE_OPTIONS.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <div className="form-text">
                Voting type is selected when the event is created and should not be changed later.
              </div>
            </div>

            <div className="col-md-12">
              <label className="form-label">Event Website</label>

              <input
                name="eventWebsite"
                value={form.eventWebsite}
                onChange={handleChange}
                className="form-control"
                placeholder="https://example.com"
              />
            </div>

            <div className="col-md-12">
              <EventVisibilityControl
                publicEvent={form.publicSeo}
                onChange={(publicSeo) => setForm((current) => ({ ...current, publicSeo }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Event Owner Name*</label>
              <input
                name="contactName"
                value={form.contactName}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Event Owner Email*</label>
              <input
                type="email"
                name="contactEmail"
                value={form.contactEmail}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Event Owner Phone*</label>
              <input
                name="contactPhone"
                value={form.contactPhone}
                onChange={handleChange}
                className="form-control"
              />
            </div>

          </div>

          <div className="border rounded p-4 mt-4">
            <h2 className="h5 fw-bold mb-2">Power Ups</h2>

            <div className="form-text mb-3">
              Select the capabilities available to this event. Saving changes
              activates checked Power Ups and deactivates unchecked Power Ups.
            </div>

            <div className="row g-3">
              {EVENT_EDITOR_POWER_UPS.map((powerUp) => {
                const isEnabled = form.purchasedAddOns?.[powerUp.key] === true;

                return (
                  <div className="col-12 col-md-6" key={powerUp.key}>
                    <div
                      className={`border rounded p-3 h-100 ${
                        isEnabled ? "border-success bg-light" : ""
                      }`}
                    >
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`event-power-up-${powerUp.key}`}
                          checked={isEnabled}
                          onChange={() => handlePurchasedAddOnChange(powerUp.key)}
                        />

                        <label
                          className="form-check-label fw-bold"
                          htmlFor={`event-power-up-${powerUp.key}`}
                        >
                          {powerUp.icon} {powerUp.name}
                        </label>
                      </div>

                      <div className="form-text mt-2">
                        {powerUp.description}
                      </div>

                      <div className="mt-2">
                        <span className="badge bg-success">
                          {form.demo ? "Demo" : powerUp.priceDisplay}
                        </span>

                        {isEnabled && (
                          <span className="badge bg-primary ms-2">
                            Enabled
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button type="submit" className="btn btn-primary mt-4">
            💾 {editing ? "Save Changes" : "Create Event"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default AdminEventEdit;
